import type { PropType } from 'vue';
import type { TRadioProps } from './TRadio.types';
import { merge } from 'es-toolkit/compat';
import { computed, defineComponent, ref, watch } from 'vue';
import { useStyles } from './TRadio.styles';

const defaultProps = {
  labelPosition: 'right',
};

export const TRadio = defineComponent({
  name: 'TRadio',
  props: {
    id: String as PropType<TRadioProps['id']>,
    label: String as PropType<TRadioProps['label']>,
    value: {
      type: [
        String,
        Number,
        Boolean,
        Object,
        Array,
        Function,
        Date,
      ] as PropType<TRadioProps['value']>,
      default: null,
    },
    modelValue: {
      type: [
        String,
        Number,
        Boolean,
        Object,
        Array,
        Function,
        Date,
      ] as PropType<TRadioProps['modelValue']>,
      default: null,
    },
    disabled: Boolean as PropType<TRadioProps['disabled']>,
    labelPosition: String as PropType<TRadioProps['labelPosition']>,
  },
  emits: ['input', 'change', 'update:modelValue'],
  setup(_props, { emit, attrs }) {
    const props = computed(() => merge({}, defaultProps, _props, attrs));

    const checked = ref(props.value.modelValue === props.value.value);
    watch(
      () => [props.value.modelValue, props.value.value],
      () => {
        checked.value = props.value.modelValue === props.value.value;
      },
    );

    const baseClass = useStyles();
    const classes = computed(() => [
      baseClass.value,
      `${baseClass.value}--label-${props.value.labelPosition}`,
      {
        [`${baseClass.value}--checked`]: checked.value,
        [`${baseClass.value}--disabled`]: props.value.disabled,
      },
    ]);

    const handleInput = () => {
      emit('update:modelValue', props.value.value);
      emit('change', props.value.value);
      emit('input', props.value.value);
      checked.value = true;
    };
    const handleClick = () => {
      if (!props.value.disabled) {
        handleInput();
      }
    };
    const handleKeydown = (event: KeyboardEvent) => {
      if (
        (event.key === 'Enter' || event.key === ' ')
        && !props.value.disabled
      ) {
        handleInput();
      }
    };

    return () => (
      <div class={classes.value}>
        <input
          id={props.value.id}
          type="radio"
          class={`${baseClass.value}__radio`}
          onClick={handleClick}
          onKeydown={handleKeydown}
          disabled={props.value.disabled}
        />
        {props.value.label && (
          <label class={`${baseClass.value}__label`} for={props.value.id}>
            {props.value.label}
          </label>
        )}
      </div>
    );
  },
});
